import { defineStore, acceptHMRUpdate } from 'pinia';

import { getServices } from '~/db/services';

import { getToDayDateIso } from '~/lib/utils/date';

export const useGamingStore = defineStore('GamingStore', () => {
  if (!useAppRunningStore().isRunning) return;

  const { $db } = useNuxtApp();
  const { gamingsService } = getServices($db);

  const data = ref<Gaming>(null);

  const { gaming: gamingDataSync } = useGamingSyncGet();

  watchEffect(() => {
    if (gamingDataSync.value && gamingDataSync.value.length > 0) {
      data.value = gamingDataSync.value[0].data;
    } else {
      data.value = null;
    }
  });

  const loadFromClient = async () => {
    const context = useContextStore();
    const gamingData = await gamingsService.get({ id: context.userId });
    if (gamingData) {
      forceUpdateRawValue(gamingData.data);
      return true;
    }
    return false;
  };

  const forceUpdateRawValue = (rawValue) => {
    globalThis.$logger.info('useGamingStore forceUpdateRawValue', rawValue);
    data.value = rawValue;
  };

  const level = computed<number>(() => data.value?.level ?? 0);

  const xp = computed<number>(() => data.value?.xp ?? 0);

  const streak = computed<number>(() => data.value?.streak ?? 0);

  const bestStreak = computed<number>(() => data.value?.bestStreak ?? 0);

  const streakHistory = computed(() => data.value?.streakHistory ?? []);

  const xpRewards = computed<{ dailyQuiz: number }>(() => {
    return {
      dailyQuiz: 100,
      ...(data.value?.xpRewards ?? {}),
    };
  });
  const levelDetails = computed<GamingLevelDetail[]>(
    () => data.value?.levelDetails ?? []
  );

  const bonusDetails = computed<GamingBonusDetail[]>(() => {
    const details = data.value?.bonusDetails ?? [];
    const toDay = getToDayDateIso();
    return details.map((detail) => {
      detail.expiresAt = !detail.expiresAt ? toDay : detail.expiresAt;
      return detail;
    });
  });

  const nextLevel = computed<GamingLevelDetail>(() => {
    const level = levelDetails.value.find((level) => {
      return level.xp > xp.value;
    });
    return level;
  });

  const nextLevelPointsNeeded = computed(() => {
    return nextLevel.value ? nextLevel.value.xp - xp.value : 0;
  });

  const nextLevelValue = computed(() => {
    return nextLevel.value ? nextLevel.value.level : 0;
  });

  const { media } = useGamingBonusMedia(bonusDetails);

  const gamingBonusActive = useGamingBonusActive();

  const setXP = async (value: number) => {
    data.value.xp = value;
    await useGamingSave();
  };

  const setLevel = async (value: number) => {
    data.value.level = value;
    await useGamingSave();
  };

  const setStreak = async (value: number) => {
    data.value.streak = value;
    await useGamingSave();
  };

  return {
    data: data as Readonly<Ref<Gaming>>,
    forceUpdateRawValue,
    loadFromClient,
    level,
    xp,
    setXP,
    setLevel,
    setStreak,
    streak,
    bestStreak,
    levelDetails,
    bonusDetails,
    nextLevelValue,
    nextLevelPointsNeeded,
    media,
    streakHistory,
    xpRewards,
    ...gamingBonusActive,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useGamingStore, import.meta.hot));
}
